import { Link } from "react-router-dom";
import { Check, Lock } from "lucide-react";

/**
 * Visible 4-step onboarding progress bar surfaced on the public hero,
 * register, KYC, and accredited-verification pages so every prospect /
 * new member can answer the question: "where am I in the process?".
 *
 * Steps map to canonical EchoSense onboarding milestones:
 *   1. Create Account
 *   2. Verify Identity (KYC documents)
 *   3. Confirm Accreditation (qualified investor review)
 *   4. Access Member Portal (funded + qualified)
 *
 * Usage:
 *   <OnboardingProgressBar currentStep={2} />
 *
 * The component is intentionally simple — big numbered circles, plain
 * English labels, no jargon. Designed to be readable at a glance from
 * mobile and desktop.
 */

const STEPS = [
  { n: 1, label: "Create Account",        to: "/register" },
  { n: 2, label: "Verify Identity",       to: "/kyc" },
  { n: 3, label: "Confirm Accreditation", to: "/kyc-verification" },
  { n: 4, label: "Access Member Portal",  to: "/dashboard" },
];

const OnboardingProgressBar = ({ currentStep = 1, variant = "default" }) => {
  const isPublic = variant === "public"; // public landing/register — no auth links

  return (
    <div
      className="w-full bg-surface border border-gold/20 rounded-xl p-4 sm:p-5"
      data-testid="onboarding-progress-bar"
    >
      <p className="text-gold text-[10px] sm:text-[11px] tracking-[0.3em] uppercase font-semibold mb-3 sm:mb-4 text-center">
        Your 4 Simple Steps
      </p>
      <ol className="flex items-stretch justify-between gap-1 sm:gap-3">
        {STEPS.map((s, i) => {
          const done    = s.n <  currentStep;
          const active  = s.n === currentStep;
          const locked  = s.n >  currentStep;

          const circleClasses = done
            ? "bg-emerald-500 text-white border-emerald-500"
            : active
              ? "bg-gold text-navy-dark border-gold shadow-lg shadow-gold/30 scale-110"
              : "bg-surface-highlight text-platinum/40 border-platinum/20";

          const labelClasses = done
            ? "text-platinum/80"
            : active
              ? "text-cream font-semibold"
              : "text-platinum/40";

          const Inner = (
            <div className="flex flex-col items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
              <div
                className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full border-2 flex items-center justify-center text-base sm:text-lg font-bold transition-all ${circleClasses}`}
                data-testid={`onboarding-step-${s.n}-circle`}
              >
                {done ? <Check size={18} /> : locked ? <Lock size={14} /> : s.n}
              </div>
              <p className={`text-[10px] sm:text-xs text-center leading-tight px-0.5 ${labelClasses}`}>
                {s.label}
              </p>
            </div>
          );

          return (
            <li key={s.n} className="flex items-stretch flex-1 min-w-0">
              {isPublic ? (
                <div className="flex-1 min-w-0">{Inner}</div>
              ) : (
                <Link
                  to={s.to}
                  className="flex-1 min-w-0 hover:opacity-90 transition-opacity"
                  data-testid={`onboarding-step-${s.n}-link`}
                >
                  {Inner}
                </Link>
              )}
              {i < STEPS.length - 1 && (
                <div className="flex items-center justify-center px-0.5 sm:px-1 mt-4 sm:mt-5">
                  <div
                    className={`h-0.5 w-3 sm:w-8 ${
                      done ? "bg-emerald-500" : "bg-platinum/15"
                    }`}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default OnboardingProgressBar;
